import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useSeo, getPortfolio, type SiteSettings, type PortfolioProject } from "@/lib/publicContent";

export default function PortfolioPage() {
  const settings = useOutletContext<SiteSettings | null>();
  const [projects, setProjects] = useState<PortfolioProject[] | null>(null);

  useSeo({
    title: `Portfolio — ${settings?.site_name || "Bonker"}`,
    description: "Sneaker custom realizzate da Bonker: anime, film, videogiochi e molto altro, dipinte a mano.",
  });

  useEffect(() => { getPortfolio().then(setProjects).catch(() => setProjects([])); }, []);

  return (
    <div data-testid="portfolio-page">
      <header className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-12">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-secondary">Il nostro lavoro</span>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-3">Portfolio</h1>
        <p className="text-muted-foreground mt-5 max-w-lg">Ogni paio è un pezzo unico. Lasciati ispirare e poi crea il tuo.</p>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-10 pb-24">
        {projects === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : projects.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="portfolio-grid">
            {projects.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}>
                <div className="group relative overflow-hidden border border-white/10 bg-card aspect-[4/5]" data-testid="portfolio-card">
                  {p.cover_image_url ? (
                    <img src={mediaUrl(p.cover_image_url)} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/5"><span className="font-display font-black text-5xl text-white/10">{p.title.slice(0, 2).toUpperCase()}</span></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-5">
                    <h2 className="font-display font-bold text-lg">{p.title}</h2>
                    {p.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.description}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/15 p-16 text-center" data-testid="portfolio-empty">
            <p className="font-display font-bold text-2xl mb-2">Presto online</p>
            <p className="text-muted-foreground mb-8">Stiamo preparando la galleria. Nel frattempo, configura la tua sneaker.</p>
            <Link to="/configuratore" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-7 py-4 hover:-translate-y-[2px] transition-transform">
              Configura la tua <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
